import { Show, type Component } from 'solid-js';

/** Percentage of a maximum, clamped to 0–100. */
export function percent(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(0, Math.min(100, (current / max) * 100));
}

/** Below this fraction of health the orb starts pulsing. */
export const CRITICAL_HEALTH = 0.25;

/**
 * Which vital an orb is showing. The two differ in colour and in nothing else.
 */
export type Vital = 'health' | 'mana';

export interface VitalOrbProps {
  vital: Vital;
  current: number;
  max: number;
  /** For the ARIA label. "Health" or "Mana". */
  label: string;
  /**
   * True when the orb should pulse. Health only, and only below
   * `CRITICAL_HEALTH` — the one piece of HUD motion that carries information
   * rather than atmosphere, which is precisely why mana never gets it.
   */
  critical?: boolean;
  /** The orb itself. */
  testId: string;
  /**
   * The number inside the glass.
   *
   * Named separately rather than derived from `testId`, because the two ids the
   * HUD already had are `health-orb`/`health-value` and `mana` — deriving one from
   * the other would have renamed test hooks that predate this component for no
   * reason a reader could see.
   */
  valueTestId: string;
}

/**
 * A glass vessel that drains downward — health on the left of the action bar,
 * mana on the right.
 *
 * **One component for both, and that is the point of the file existing.** The bar
 * had a 96px health orb on the left and mana as an 8px line tucked under the skill
 * row: two readouts of the same *kind* of fact, drawn two entirely different ways,
 * so a player could read their health at a glance and had to hunt for their mana.
 * Two nearly-identical orb components would have been the other version of the
 * same mistake — the reason there is exactly one button class in `base.css`.
 *
 * The number is printed inside the glass. Rounded, because mana regenerates
 * fractionally (`mana_regen_flat` is one of the two fractional stat keys) and a
 * vital that flickers through three decimals is unreadable; the *fill* is drawn
 * from the unrounded value, so the fluid moves smoothly even while the digits sit
 * still.
 */
export const VitalOrb: Component<VitalOrbProps> = (props) => (
  <div
    class="orb"
    classList={{
      [`orb--${props.vital}`]: true,
      'orb--critical': props.critical === true,
    }}
    data-testid={props.testId}
    role="progressbar"
    aria-label={props.label}
    aria-valuenow={Math.round(props.current)}
    aria-valuemin={0}
    aria-valuemax={props.max}
  >
    <div class="orb__fill" style={{ height: `${percent(props.current, props.max)}%` }} />
    <div class="orb__glass" />
    <div class="orb__value" data-testid={props.valueTestId}>
      {Math.round(props.current)}
    </div>
    {/* The maximum, under the current value and small. It is the difference
        between "40" meaning nearly dead and meaning barely scratched, and it was
        absent from the health orb entirely. */}
    <Show when={props.max > 0}>
      <div class="orb__max" aria-hidden="true">
        /{props.max}
      </div>
    </Show>
  </div>
);
